<template>
  <div>
    <UploadImageCautionArea :cautions="cautions" class="mb-4" />
    <ValidationProvider
      v-slot="validationState"
      name="証明書の種類"
      tag="div"
      rules="required"
      vid="identificationType"
    >
      <FormSelectGroup
        label="身分証明書"
        appLabelText="必須"
        :value="inputValues.identificationType"
        :options="selectedOptions"
        :disabled="disabled"
        :validation-state="validationState"
        class="mb-4"
        @input="
          onChangeIdentificationType($event, validationState.handleChange)
        "
      />
    </ValidationProvider>
    <UploadImageForm
      v-model="inputValues.identificationImage"
      title="写真アップロード（オモテ面）"
      appLabelText="必須"
      appLabelVariant="red"
      photoGraphingType="id"
      :isFrontCamera="false"
      v-model:isCameraActive="isCameraActiveValue"
      :options="options"
      :disabled="disabled"
      :disabledText="disabledText"
      class="mb-4"
      imageDescription="表面"
    >
      <IdImage :imageUrl="inputValues.identificationImage" />
    </UploadImageForm>
    <UploadImageForm
      v-if="isShowBackImage"
      v-model="inputValues.identificationBackImage"
      title="写真アップロード（ウラ面）"
      appLabelText="任意"
      appLabelVariant="mild"
      photoGraphingType="id"
      :isFrontCamera="false"
      v-model:isCameraActive="isCameraActiveValue"
      :options="options"
      :disabled="disabled"
      :disabledText="disabledText"
      class="mb-4"
      imageDescription="裏面"
    >
      <IdImage :imageUrl="inputValues.identificationBackImage" />
    </UploadImageForm>
  </div>
</template>

<script lang="ts">
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import IdImage from "@/components/molecules/other/IdImage.vue";
import UploadImageCautionArea from "@/components/molecules/other/UploadImageCautionArea.vue";
import UploadImageForm from "@/components/organisms/register/UploadImageForm.vue";
import { identification } from "@/models/identification";
import { authStore } from "@/store/auth";
import { IdentificationType } from "@/types/IdentificationType";
import { IdentifierInputValues } from "@/types/user/RequestVerificationQueueBody";
import { HandleChangeType } from "@/types/Validation";
import { Component, Prop, Vue } from "vue-facing-decorator";

@Component({
  components: {
    FormSelectGroup,
    UploadImageForm,
    UploadImageCautionArea,
    IdImage
  }
})
export default class SetId extends Vue {
  @Prop({
    type: Object,
    required: true
  })
  value!: IdentifierInputValues;

  @Prop({
    type: Boolean,
    required: true
  })
  isCameraActive!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  disabled!: boolean;

  @Prop({
    type: String,
    default: ""
  })
  disabledText!: string;

  @Prop({
    type: Boolean,
    default: false
  })
  isVerified!: boolean;

  get inputValues() {
    return this.value;
  }

  set inputValues(v) {
    if (this.value !== v) this.$emit("input", v);
  }

  get isCameraActiveValue() {
    return this.isCameraActive;
  }

  set isCameraActiveValue(v) {
    this.$emit("update:isCameraActive", v);
  }

  get selectedOptions() {
    // 平成27年に住民基本台帳カードが廃止されたため、選択肢から除外する
    // ただし既に住民基本台帳カードで登録されているワーカーもいるため、その場合は表示のため選択肢に残す
    if (this.inputValues.identificationType === "citizen_identification_card") {
      return identification;
    }
    return identification.filter(
      ident => ident.value !== "citizen_identification_card"
    );
  }

  get options() {
    return [
      "[身分証の全体]が入っていますか？",
      "反射なく、はっきり読み取れますか？"
    ];
  }

  get cautions() {
    return [
      "四隅が写真に収まっている",
      "印字内容が鮮明",
      "関係ないものが写っていない"
    ];
  }

  get isShowBackImage() {
    return this.inputValues.identificationType !== "my_number_card";
  }

  async onChangeIdentificationType(
    newValue: IdentificationType,
    handleChange: HandleChangeType
  ) {
    const oldValue = this.inputValues.identificationType;
    this.inputValues.identificationType = newValue;
    if (
      oldValue !== null &&
      this.isVerified &&
      authStore.user?.identificationType !== newValue
    ) {
      const isOk = await this.$bvModal.msgBoxConfirm(
        "認証済みの身分証明書の種類を変更すると、画像の再アップロードが必要になります。よろしいですか？",
        {
          centered: true,
          cancelTitle: "キャンセル",
          cancelVariant: "mild"
        }
      );
      if (!isOk) {
        // 変更前の値に戻す
        this.inputValues.identificationType = oldValue;
        handleChange(oldValue, true);
        return;
      }
      this.inputValues.identificationImage = null;
      this.inputValues.identificationBackImage = null;
    }
  }
}
</script>
